import { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import api_endpoints from "../../api";
import { useApiClientMutation, useApiClientQuery } from "../../hooks/useApiClient";
import { useCustomTranslation } from "../../hooks/useCustomTranslation";
import { translateApiError } from "../../utils/apiErrorTranslation";
import { currencyFormatter, dateTimeReadableSimple } from "../../utils";
import { STATUS_TYPE } from "../../enums/PromiseEnums";
import PromiseReminderHistory from "../CampaignPromise/components/PromiseReminderHistory";

const PromiseFollowUpTab = ({ donorId, organizationId }) => {
  const { t } = useCustomTranslation("Promise Details");
  const [reminderRefreshKey, setReminderRefreshKey] = useState(0);
  const [successMessage, setSuccessMessage] = useState("");
  const [sendingPromiseId, setSendingPromiseId] = useState("");

  const promisesQuery = useApiClientQuery(
    () => api_endpoints.getDonorPromises(donorId, organizationId),
    {
      queryKey: ["donor_promises", donorId, organizationId],
      enabled: Boolean(donorId && organizationId),
    }
  );

  const sendLinkMutation = useApiClientMutation(
    ({ promiseId }) => api_endpoints.sendPromisePaymentLink(promiseId),
    [],
    () => {
      setSuccessMessage(
        t("promise_payment_link_sent", "Payment link sent to the donor.")
      );
      setReminderRefreshKey((value) => value + 1);
      setSendingPromiseId("");
    },
    () => {
      setSendingPromiseId("");
      setReminderRefreshKey((value) => value + 1);
    }
  );

  const promises = Array.isArray(promisesQuery.data)
    ? promisesQuery.data
    : [];

  const totals = useMemo(
    () =>
      promises.reduce(
        (summary, promise) => {
          summary.promised += Number(promise.promise_amount || 0);
          summary.given += Number(promise.given_amount || 0);
          summary.remaining += Number(
            promise.remaining_amount ??
              Math.max(
                0,
                Number(promise.promise_amount || 0) -
                  Number(promise.given_amount || 0)
              )
          );
          return summary;
        },
        { promised: 0, given: 0, remaining: 0 }
      ),
    [promises]
  );

  const handleSendPaymentLink = (promiseId) => {
    setSuccessMessage("");
    setSendingPromiseId(promiseId);
    sendLinkMutation.mutate({ promiseId });
  };

  if (promisesQuery.isLoading) {
    return (
      <Box className="flex justify-center py-8">
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Stack spacing={3} sx={{ mt: 2 }}>
      {successMessage ? (
        <Alert severity="success" onClose={() => setSuccessMessage("")}>
          {successMessage}
        </Alert>
      ) : null}
      {sendLinkMutation.isError ? (
        <Alert severity="error">
          {translateApiError(
            t,
            sendLinkMutation.error?.response?.data?.message || "promise_payment_link_failed",
            "Failed to send payment link."
          )}
        </Alert>
      ) : null}
      {promisesQuery.isError ? (
        <Alert severity="error">
          {t("donor_promises_load_error", "Unable to load donor promises.")}
        </Alert>
      ) : null}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "repeat(3, minmax(0, 1fr))",
          },
          gap: 2,
        }}
      >
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography color="text.secondary">
            {t("promised_amount", "Promised Amount")}
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {currencyFormatter(totals.promised)}
          </Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography color="text.secondary">
            {t("given_amount", "Given Amount")}
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {currencyFormatter(totals.given)}
          </Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography color="text.secondary">
            {t("amount_left", "Amount Left")}
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {currencyFormatter(totals.remaining)}
          </Typography>
        </Paper>
      </Box>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
          {t("promises", "Promises")}
        </Typography>
        {!promises.length ? (
          <Typography color="text.secondary">
            {t("no_promises_for_donor", "This donor has no promises.")}
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("campaign", "Campaign")}</TableCell>
                  <TableCell>{t("created_at", "Created At")}</TableCell>
                  <TableCell align="right">
                    {t("promised_amount", "Promised")}
                  </TableCell>
                  <TableCell align="right">
                    {t("given_amount", "Given")}
                  </TableCell>
                  <TableCell align="right">
                    {t("amount_left", "Amount Left")}
                  </TableCell>
                  <TableCell>{t("status", "Status")}</TableCell>
                  <TableCell>{t("type_of_payment", "Payment Type")}</TableCell>
                  <TableCell align="right">{t("actions", "Actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {promises.map((promise) => {
                  const remaining = Number(
                    promise.remaining_amount ??
                      Math.max(
                        0,
                        Number(promise.promise_amount || 0) -
                          Number(promise.given_amount || 0)
                      )
                  );
                  const isSending =
                    sendLinkMutation.isPending &&
                    String(sendingPromiseId) === String(promise._id);
                  const hasActiveSchedule = ["active", "paused"].includes(
                    promise.subscription_id?.status
                  );
                  const statusChip = {
                    [STATUS_TYPE.IN_PROGRESS]: { color: "primary" },
                    [STATUS_TYPE.ABANDONED]: {
                      color: "error",
                      variant: "outlined",
                    },
                    [STATUS_TYPE.CLOSED]: { color: "success" },
                    [STATUS_TYPE.OPEN]: { color: "success" },
                    [STATUS_TYPE.CANCELLED]: { color: "warning" },
                  }[promise.status] || { color: "default" };

                  return (
                    <TableRow key={promise._id} hover>
                      <TableCell>{promise.campaign?.name || "-"}</TableCell>
                      <TableCell>
                        {dateTimeReadableSimple(promise.created_at)}
                      </TableCell>
                      <TableCell align="right">
                        {currencyFormatter(Number(promise.promise_amount || 0))}
                      </TableCell>
                      <TableCell align="right">
                        {currencyFormatter(Number(promise.given_amount || 0))}
                      </TableCell>
                      <TableCell align="right">
                        {currencyFormatter(remaining)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={t(promise.status, promise.status)}
                          color={statusChip.color}
                          variant={statusChip.variant || "filled"}
                        />
                      </TableCell>
                      <TableCell>
                        {t(promise.payment_type, promise.payment_type)}
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<SendIcon />}
                          disabled={
                            remaining <= 0 ||
                            ["closed", "abandoned", "cancelled"].includes(promise.status) ||
                            hasActiveSchedule ||
                            sendLinkMutation.isPending
                          }
                          onClick={() => handleSendPaymentLink(promise._id)}
                        >
                          {isSending
                            ? t("sending", "Sending...")
                            : t("send_payment_link", "Send Payment Link")}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <PromiseReminderHistory
        organizationId={organizationId}
        donorId={donorId}
        refreshKey={reminderRefreshKey}
      />
    </Stack>
  );
};

export default PromiseFollowUpTab;
